import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { GATEWAY_HTTP_URL } from '../config/api';
import { useOrganization } from '../context/OrganizationContext';

export interface ApiToken {
  id: string;
  name: string;
  prefix: string;
  userId: string;
  createdAt: string;
  lastUsedAt: string | null;
}

/**
 * Owner of a freshly-minted PAT. `rawToken` is returned exactly once by
 * `POST /api/api-tokens` and never persisted server-side -- callers MUST
 * show it to the user immediately with a warning that it cannot be retrieved.
 */
export interface CreatedToken {
  token: ApiToken;
  rawToken: string;
}

interface UseApiTokensResult {
  tokens: ApiToken[];
  isLoading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
  createToken: (name: string) => Promise<CreatedToken>;
  revokeToken: (id: string) => Promise<void>;
}

export function useApiTokens(): UseApiTokensResult {
  const { getAuthHeaders, currentOrganization } = useOrganization();
  const [tokens, setTokens] = useState<ApiToken[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    if (!currentOrganization) {
      setTokens([]);
      return;
    }
    setIsLoading(true);
    setError(null);
    try {
      const headers = await getAuthHeaders();
      const res = await axios.get(`${GATEWAY_HTTP_URL}/api/api-tokens`, { headers });
      setTokens(res.data?.tokens ?? []);
    } catch (err: any) {
      const msg = err?.response?.data?.error ?? err?.message ?? 'Failed to load API tokens';
      setError(msg);
      setTokens([]);
    } finally {
      setIsLoading(false);
    }
  }, [getAuthHeaders, currentOrganization]);

  const createToken = useCallback(
    async (name: string): Promise<CreatedToken> => {
      const headers = await getAuthHeaders();
      const res = await axios.post(
        `${GATEWAY_HTTP_URL}/api/api-tokens`,
        { name },
        { headers },
      );
      // Refresh list in the background. The caller shows the raw token from
      // the response itself; the list reflects the new row on the next render.
      refresh();
      return { token: res.data.token, rawToken: res.data.rawToken };
    },
    [getAuthHeaders, refresh],
  );

  const revokeToken = useCallback(
    async (id: string) => {
      const headers = await getAuthHeaders();
      await axios.delete(`${GATEWAY_HTTP_URL}/api/api-tokens/${id}`, { headers });
      // Optimistically drop from local state; the next refresh will reconcile.
      setTokens((prev) => prev.filter((t) => t.id !== id));
    },
    [getAuthHeaders],
  );

  useEffect(() => {
    refresh();
  }, [refresh]);

  return { tokens, isLoading, error, refresh, createToken, revokeToken };
}
